/**
 * Tamaño en pantalla de una foto girada para que quepa ENTERA en su panel.
 *
 * Al girar 90° el ancho de la foto pasa a ocupar el alto del panel. Con `object-contain` y la
 * rotación por CSS, la caja de la imagen se medía sin girar: la foto acostada se salía del panel o
 * quedaba diminuta. Aquí se calcula la escala con los lados intercambiados y el visor pinta la
 * imagen con ese ancho y alto, centrada, antes de girarla.
 *
 * Puro: corre en el vitest de node.
 */

export type Tamano = { w: number; h: number };

/** Giro normalizado a 0, 90, 180 o 270. */
export const normalizarGiro = (grados: number) => (((Math.round(grados / 90) * 90) % 360) + 360) % 360;

/**
 * Ancho y alto (sin girar) con que se pinta la imagen. La escala es la mayor que deja ver la foto
 * completa dentro de `caja` menos `margen` por lado, ya girada.
 */
export function tamanoImagenGirada(natural: Tamano, caja: Tamano, giro: number, margen = 16): Tamano | null {
  if (!(natural.w > 0 && natural.h > 0 && caja.w > 0 && caja.h > 0)) return null;
  const acostada = normalizarGiro(giro) % 180 !== 0;
  const disponibleW = Math.max(1, caja.w - margen * 2);
  const disponibleH = Math.max(1, caja.h - margen * 2);
  // Lo que ocupa en pantalla ya girada: con 90° el ancho visible es el alto de la foto.
  const visibleW = acostada ? natural.h : natural.w;
  const visibleH = acostada ? natural.w : natural.h;
  const escala = Math.min(disponibleW / visibleW, disponibleH / visibleH);
  return { w: Math.floor(natural.w * escala), h: Math.floor(natural.h * escala) };
}
